/**
 * Every colour the public event page derives from an organiser's theme, in one
 * pure function, each one checked against the surface it actually sits on.
 *
 * Before this, ThemedEventPage worked out its surfaces inline, and two of them
 * decided "is this page light?" by looking at the TEXT colour rather than the
 * page. A mid-tone text colour (Grit & Grace's orange on navy) counts as dark
 * by that test, so the page was treated as light: raised surfaces came out
 * white, which painted the category pills as empty white blobs and turned a
 * hovered ticket row white with orange text. The disabled "Continue" button
 * was the accent at 35% opacity, which on navy is a dull purple with a label
 * nobody can read.
 *
 * The rules now:
 *   - light or dark is decided by the colour a thing sits on, never by the
 *     text colour;
 *   - an organiser's colour is kept whenever it is readable where it is used,
 *     and swapped for plain white or near-black only when it is not, so
 *     existing pages that read fine look exactly as they did;
 *   - disabled states are a solid, quiet fill with a label that still reads.
 */

export interface ThemeColoursInput {
  /** Page background. */
  primary: string;
  /** Accent: buttons, links, highlights. */
  secondary: string;
  /** Body text. */
  text: string;
  /** Dates, labels, small print. */
  textMuted?: string;
  /** Text on an accent fill. */
  buttonText?: string;
  /** Headings. Absent means "the same as the body text", as every page was before. */
  heading?: string;
}

export type BoxStyle = 'white' | 'black' | 'transparent';

/** Card outlines (Branding > Page theme > Borders). Absent means today's derived hairline. */
export interface BorderOptions {
  style?: 'subtle' | 'none';
  /** Replaces the derived outline when borders are on. Unparseable is ignored. */
  colour?: string;
  /**
   * Box fill (Branding > Page theme > Box fill). Absent or 'filled' is every
   * page so far. 'none' takes the fill off the section boxes: they sit on the
   * page background, whatever the box style, and every ink is checked against
   * the page. Menus and dialogs keep a solid surface.
   */
  boxFill?: 'filled' | 'none';
}

/** Text on a surface needs this much contrast before we keep the organiser's choice. */
export const MIN_TEXT = 3.5;
/** Large text (headings) and secondary text: WCAG's large-text bar. */
export const MIN_LARGE = 3;

type Rgb = [number, number, number];

/** Parse #rgb, #rgba, #rrggbb, #rrggbbaa and rgb()/rgba(). Alpha is ignored. */
export function parseColour(value: string | undefined | null): Rgb | null {
  if (!value || typeof value !== 'string') return null;
  const v = value.trim();
  const hex = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(v);
  if (hex) {
    let h = hex[1];
    if (h.length <= 4) h = h.slice(0, 3).split('').map((c) => c + c).join('');
    h = h.slice(0, 6);
    const n = parseInt(h, 16);
    return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
  }
  const fn = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(v);
  if (fn) {
    const out = [fn[1], fn[2], fn[3]].map((s) => Math.max(0, Math.min(255, Math.round(Number(s)))));
    if (out.every((n) => Number.isFinite(n))) return out as Rgb;
  }
  const named: Record<string, Rgb> = { white: [255, 255, 255], black: [0, 0, 0] };
  return named[v.toLowerCase()] ?? null;
}

export function toHex(rgb: Rgb): string {
  return `#${rgb.map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

function luminance([r, g, b]: Rgb): number {
  const [lr, lg, lb] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

/** WCAG contrast ratio, 1 to 21. Unparseable colours count as 1 (no contrast). */
export function contrast(a: string, b: string): number {
  const ra = parseColour(a);
  const rb = parseColour(b);
  if (!ra || !rb) return 1;
  const la = luminance(ra);
  const lb = luminance(rb);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Dark by what it is, measured against white and near-black, not by a brightness cut-off. */
export function isDarkSurface(colour: string): boolean {
  if (!parseColour(colour)) return false;
  return contrast(colour, '#FFFFFF') >= contrast(colour, '#111111');
}

/** Blend `b` into `a` by `amount` (0 keeps a, 1 is b). Opaque hex. */
export function mix(a: string, b: string, amount: number): string {
  const ra = parseColour(a);
  const rb = parseColour(b);
  if (!ra || !rb) return a;
  return toHex(ra.map((c, i) => c + (rb[i] - c) * amount) as Rgb);
}

/** `#rrggbbaa` for a parseable colour, otherwise the colour unchanged. */
export function withAlpha(colour: string, alpha: number): string {
  const rgb = parseColour(colour);
  if (!rgb) return colour;
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255).toString(16).padStart(2, '0');
  return `${toHex(rgb)}${a}`.toUpperCase();
}

/** A `#rrggbbaa` colour as it actually shows on `surface`; anything else unchanged. */
export function overSurface(colour: string, surface: string): string {
  const m = /^#([0-9a-f]{6})([0-9a-f]{2})$/i.exec(colour.trim());
  if (!m || !parseColour(surface)) return colour;
  return mix(surface, `#${m[1]}`, parseInt(m[2], 16) / 255);
}

/** Plain white or near-black, whichever reads better on `surface`. */
export function bestInk(surface: string): string {
  return contrast(surface, '#FFFFFF') >= contrast(surface, '#111111') ? '#FFFFFF' : '#111111';
}

/**
 * The organiser's colour when it reads on `surface` (against every surface
 * given, when it sits on more than one), otherwise plain white or black.
 * Unparseable colours are passed through untouched: we cannot measure them,
 * and they were valid CSS when they were saved.
 */
export function readable(preferred: string | undefined, surface: string | string[], minimum = MIN_TEXT): string {
  const surfaces = (Array.isArray(surface) ? surface : [surface]).filter((s) => parseColour(s));
  if (surfaces.length === 0) return preferred || '#111111';
  const base = surfaces[0];
  if (!preferred) return bestInk(base);
  if (!parseColour(preferred)) return preferred;
  return surfaces.every((s) => contrast(preferred, s) >= minimum) ? preferred : bestInk(base);
}

/**
 * Muted text: keep it when it reads, otherwise move it towards the body text
 * in steps (so it stays "the quieter one") before giving up and using the
 * body text itself.
 */
function readableMuted(muted: string | undefined, text: string, surfaces: string[]): string {
  if (!muted) return text;
  if (!parseColour(muted)) return muted;
  const ok = (c: string) => surfaces.every((s) => !parseColour(s) || contrast(c, s) >= MIN_LARGE);
  if (ok(muted)) return muted;
  for (const step of [0.35, 0.6, 0.8]) {
    const c = mix(muted, text, step);
    if (ok(c)) return c;
  }
  return text;
}

/**
 * A disabled button: a solid fill a step off its surface, and a label that
 * still reads on it (3:1, the bar for large and non-essential text). Opacity
 * is never used, because a faded accent over a dark page goes muddy.
 */
export function disabledPair(surface: string, ink: string): { bg: string; text: string; border: string } {
  if (!parseColour(surface) || !parseColour(ink)) {
    return { bg: 'rgba(127,127,127,0.18)', text: 'rgba(127,127,127,0.9)', border: 'rgba(127,127,127,0.3)' };
  }
  const bg = mix(surface, ink, 0.12);
  let text = ink;
  for (const step of [0.45, 0.35, 0.25, 0.15]) {
    const c = mix(ink, surface, step);
    if (contrast(c, bg) >= MIN_LARGE) { text = c; break; }
  }
  if (contrast(text, bg) < MIN_LARGE) text = bestInk(bg);
  return { bg, text, border: mix(surface, ink, 0.22) };
}

export interface DerivedThemeColours {
  page: string;
  accent: string;
  /** Text on an accent fill. */
  onAccent: string;
  /** The accent where it is used as text or an icon straight on the page. */
  accentInk: string;
  heading: string;
  text: string;
  textMuted: string;
  card: {
    bg: string;
    text: string;
    heading: string;
    muted: string;
    border: string;
    borderStrong: string;
    ticketBorder: string;
    /**
     * Outline of form inputs and other controls. Equal to `border` by default;
     * with borders off it keeps the derived line, so an input never loses its
     * edge, and a custom colour is used only when it is at least as visible.
     */
    inputBorder: string;
  };
  /** Pills, hovered rows, small raised panels. */
  raised: string;
  /** Menus and dialogs that float over the page. */
  overlay: string;
  /** Row hover and secondary fills. */
  bgSecondary: string;
  accentBorder: string;
  accentLight: string;
  /** Selected row fill. */
  accentBg: string;
  /** Muted text on raised surfaces (pills). */
  chipText: string;
  disabled: { bg: string; text: string; border: string };
  /** The page header/footer hairline. */
  pageHairline: string;
  /**
   * The mobile ticket sheet's top edge. Absent by default (the sheet keeps its
   * own line); 'transparent' with borders off; the custom colour when set.
   */
  sheetBorder?: string;
  /**
   * With borders off, rows that used to be told apart by an outline alone
   * (wave rows, schedule items) get this quiet tint instead. A see-through
   * tint of the box's text, so it reads on any box and on the bare page.
   * Absent when borders are on: those rows keep their outline as before.
   */
  rowFill?: string;
}

/**
 * Work out every surface and ink for the page. Pure, so it is tested on its
 * own and the same answers reach the dashboard's contrast warnings.
 */
export function deriveThemeColours(colors: ThemeColoursInput, requestedBoxStyle: BoxStyle = 'white', borders: BorderOptions = {}): DerivedThemeColours {
  const page = colors.primary;
  // No fill: boxes are see-through whatever their style, so they take the
  // see-through derivation (inks checked against the page, raised surfaces
  // and menus from the page).
  const noFill = borders.boxFill === 'none';
  const boxStyle: BoxStyle = noFill ? 'transparent' : requestedBoxStyle;
  const accent = colors.secondary;
  const pageDark = isDarkSurface(page);

  const text = readable(colors.text, page, MIN_TEXT);
  const heading = readable(colors.heading || colors.text, page, MIN_LARGE);

  // The surfaces things sit on, decided by the page, not by the text.
  let card: DerivedThemeColours['card'];
  let raised: string;
  let overlay: string;
  let bgSecondary: string;
  let cardSurface: string;

  if (boxStyle === 'black') {
    card = { bg: '#000000', text: '#FFFFFF', heading: '#FFFFFF', muted: '#D1D5DB', border: '#374151', borderStrong: '#4B5563', ticketBorder: '#6B7280', inputBorder: '#374151' };
    raised = '#000000';
    overlay = '#1A1917';
    bgSecondary = '#000000';
    cardSurface = '#000000';
  } else if (boxStyle === 'transparent') {
    // A page that reads light gets white raised surfaces (as before); a dark
    // one gets its own colour lifted a little.
    raised = pageDark ? mix(page, '#FFFFFF', 0.08) : '#FFFFFF';
    overlay = pageDark ? mix(page, '#FFFFFF', 0.12) : '#FFFFFF';
    const surfaces = [page, raised];
    const cardText = readable(colors.text, surfaces, MIN_TEXT);
    const cardHeading = readable(colors.heading || colors.text, surfaces, MIN_LARGE);
    card = {
      bg: 'transparent',
      text: cardText,
      heading: cardHeading,
      muted: readableMuted(colors.textMuted, cardText, surfaces),
      border: withAlpha(cardText, 0.2),
      borderStrong: withAlpha(cardText, 0.27),
      ticketBorder: withAlpha(cardText, 0.33),
      inputBorder: withAlpha(cardText, 0.2),
    };
    bgSecondary = withAlpha(cardText, 0.08);
    cardSurface = page;
  } else {
    card = { bg: '#FFFFFF', text: '#111827', heading: '#111827', muted: '#6B7280', border: '#E5E7EB', borderStrong: '#D1D5DB', ticketBorder: '#D1D5DB', inputBorder: '#E5E7EB' };
    raised = '#F3F4F6';
    overlay = '#FFFFFF';
    bgSecondary = '#F3F4F6';
    cardSurface = '#FFFFFF';
  }

  let sheetBorder: string | undefined;
  let rowFill: string | undefined;
  if (borders.style === 'none') {
    // No outlines, so a card must stand apart by its fill alone. A box that
    // is see-through, or the same colour as the page, gets a gentle lift of
    // the page (lighter on a dark page, darker on a light one); the small
    // raised surfaces inside it lift a step further so pills still show.
    // Not when the organiser asked for no fill: then spacing and headings
    // keep the sections apart, which is the flat look they chose.
    const lift = (base: string, amount: number) => mix(base, bestInk(base) === '#FFFFFF' ? '#FFFFFF' : '#111111', amount);
    const blendsIn = card.bg === 'transparent' || !parseColour(page) || contrast(card.bg, page) < 1.15;
    if (blendsIn && parseColour(page) && !noFill) {
      const base = card.bg === 'transparent' ? page : card.bg;
      const baseDark = isDarkSurface(base);
      const bg = lift(base, baseDark ? 0.06 : 0.035);
      raised = lift(base, baseDark ? 0.13 : 0.08);
      if (card.bg === 'transparent') {
        const surfaces = [page, bg, raised];
        const cardText = readable(colors.text, surfaces, MIN_TEXT);
        card = {
          ...card,
          bg,
          text: cardText,
          heading: readable(colors.heading || colors.text, surfaces, MIN_LARGE),
          muted: readableMuted(colors.textMuted, cardText, surfaces),
          inputBorder: withAlpha(cardText, 0.2),
        };
        bgSecondary = withAlpha(cardText, 0.08);
      } else {
        card = { ...card, bg };
        bgSecondary = raised;
      }
      cardSurface = bg;
    }
    card = { ...card, border: 'transparent', ticketBorder: 'transparent' };
    sheetBorder = 'transparent';
    rowFill = parseColour(card.text) ? withAlpha(card.text, 0.07) : 'rgba(127,127,127,0.1)';
  } else if (borders.colour && parseColour(borders.colour)) {
    const c = borders.colour;
    // Decorative, so a faint pick is allowed; inputs take it only when it is
    // at least as visible as the derived line they would otherwise have.
    const surface = parseColour(cardSurface) ? cardSurface : page;
    const seen = (colour: string) => contrast(overSurface(colour, surface), surface);
    const inputBorder = seen(c) >= seen(card.inputBorder) ? c : card.inputBorder;
    card = { ...card, border: c, ticketBorder: c, inputBorder };
    sheetBorder = c;
  }

  const textMuted = readableMuted(colors.textMuted, text, [page]);
  // Button labels are short and bold, so the large-text bar applies. Holding
  // them to 3.5 would turn the Sunset preset's white-on-amber label black.
  const onAccent = readable(colors.buttonText || '#FFFFFF', accent, MIN_LARGE);

  return {
    page,
    accent,
    onAccent,
    accentInk: readable(accent, page, MIN_LARGE),
    heading,
    text,
    textMuted,
    card,
    raised,
    overlay,
    bgSecondary,
    accentBorder: withAlpha(accent, 0.4),
    accentLight: withAlpha(accent, 0.08),
    accentBg: withAlpha(accent, 0.12),
    chipText: readable(card.muted, raised, MIN_LARGE),
    // Neutral rather than the brand's text colour: a disabled button should
    // read as "not yet", not as a second, duller accent.
    disabled: disabledPair(cardSurface, bestInk(cardSurface)),
    pageHairline: withAlpha(text, 0.125),
    ...(sheetBorder ? { sheetBorder } : {}),
    ...(rowFill ? { rowFill } : {}),
  };
}

/**
 * The derived card outline as a plain opaque colour, as it shows on the card
 * (or on the page, for see-through boxes): where the Branding border colour
 * picker starts before the organiser has picked one.
 */
export function derivedBorderHex(colors: ThemeColoursInput, boxStyle: BoxStyle = 'white', boxFill: 'filled' | 'none' = 'filled'): string {
  const d = deriveThemeColours(colors, boxStyle, { boxFill });
  const surface = d.card.bg === 'transparent' ? d.page : d.card.bg;
  const flat = overSurface(d.card.border, surface);
  const rgb = parseColour(flat);
  return rgb ? toHex(rgb) : '#E5E7EB';
}

export interface ContrastWarning {
  /** Which pair, in the organiser's words. */
  pair: 'text' | 'heading' | 'muted' | 'button' | 'accent';
  ratio: number;
  message: string;
}

/**
 * The pairs an organiser chose that are hard to read, for the Branding page.
 * The page itself still fixes them at render (see deriveThemeColours); this
 * says so before they save, so the page doesn't quietly differ from the pick.
 */
export function themeContrastWarnings(colors: ThemeColoursInput): ContrastWarning[] {
  const out: ContrastWarning[] = [];
  const page = colors.primary;
  const check = (pair: ContrastWarning['pair'], fg: string | undefined, bg: string, min: number, message: string) => {
    if (!fg || !parseColour(fg) || !parseColour(bg)) return;
    const ratio = contrast(fg, bg);
    if (ratio < min) out.push({ pair, ratio: Math.round(ratio * 10) / 10, message });
  };
  check('text', colors.text, page, MIN_TEXT, 'Text is hard to read on the background. The page will use white or black instead.');
  if (colors.heading && colors.heading !== colors.text) {
    check('heading', colors.heading, page, MIN_LARGE, 'Headings are hard to read on the background. The page will use white or black instead.');
  }
  check('muted', colors.textMuted, page, MIN_LARGE, 'Muted text is hard to read on the background. The page will darken or lighten it.');
  check('button', colors.buttonText || '#FFFFFF', colors.secondary, MIN_LARGE, 'Button text is hard to read on the accent. The page will use white or black instead.');
  check('accent', colors.secondary, page, 1.5, 'The accent is very close to the background, so buttons may not stand out.');
  return out;
}
